/**
 * Поведение сайта: меню, липкая шапка, оглавление длинной страницы,
 * лайтбокс галереи и плавающая кнопка «Позвонить».
 *
 * Порт `proto.js`: те же наблюдатели, те же пороги и те же
 * data-атрибуты. Единственное отличие — форма заявки: здесь она
 * отправляется на сервер (`request-form.js`), а в прототипе имитировалась.
 *
 * Точка перелома навигации — 1150px: на ней в разметке переключается
 * `min-[1151px]`/`max-[1150px]`, и JS обязан считать так же.
 */

const MOBILE_NAV_MAX = 1150;
const DESKTOP_NAV_MIN = 1151;
const SCROLL_THRESHOLD = 8;

export function normalisePage(value) {
  return String(value || '').trim().toLowerCase() || 'home';
}

const isMobileNav = () => window.matchMedia(`(max-width: ${MOBILE_NAV_MAX}px)`).matches;

export function initSiteNavigation(root = document) {
  const toggle = root.querySelector('[data-menu-toggle]');
  const nav = root.querySelector('#main-navigation');

  if (!toggle || !nav) return;

  const setNavInert = (inert) => {
    if (isMobileNav() && nav.inert !== inert) nav.inert = inert;
  };
  const closeDropdowns = () => {
    root.querySelectorAll('[data-nav-group][data-dropdown-open="true"]').forEach((group) => {
      group.setAttribute('data-dropdown-open', 'false');
      group.querySelector(':scope > [data-nav-trigger]')?.setAttribute('aria-expanded', 'false');
      const indicator = group.querySelector(':scope > [data-nav-trigger] [data-nav-indicator]');
      if (indicator) indicator.textContent = '+';
      group.querySelector(':scope > [data-dropdown]')?.setAttribute('data-dropdown-open', 'false');
    });
  };

  let outsideClickHandler;
  const closeMenu = ({ restoreFocus = false } = {}) => {
    if (nav.getAttribute('data-menu-open') !== 'true') return;
    nav.setAttribute('data-menu-open', 'false');
    toggle.setAttribute('aria-expanded', 'false');
    setNavInert(true);
    if (outsideClickHandler) document.removeEventListener('click', outsideClickHandler);
    if (restoreFocus) toggle.focus();
  };
  const openMenu = () => {
    root.querySelector('[data-site-header]')?.removeAttribute('data-header-hidden');
    nav.setAttribute('data-menu-open', 'true');
    toggle.setAttribute('aria-expanded', 'true');
    setNavInert(false);
    outsideClickHandler = (event) => {
      if (event.target instanceof Node && !nav.contains(event.target) && !toggle.contains(event.target)) {
        closeMenu();
      }
    };
    document.addEventListener('click', outsideClickHandler);
  };

  toggle.addEventListener('click', (event) => {
    event.stopPropagation();
    if (nav.getAttribute('data-menu-open') === 'true') closeMenu();
    else openMenu();
  });

  root.querySelectorAll('[data-nav-group]').forEach((group) => {
    const trigger = group.querySelector(':scope > [data-nav-trigger]');
    const dropdown = group.querySelector(':scope > [data-dropdown]');
    if (!trigger || !dropdown) return;
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-haspopup', 'true');
    const setOpen = (open) => {
      const value = String(open);
      trigger.setAttribute('aria-expanded', value);
      group.setAttribute('data-dropdown-open', value);
      dropdown.setAttribute('data-dropdown-open', value);
      const indicator = trigger.querySelector('[data-nav-indicator]');
      if (indicator) indicator.textContent = open ? '−' : '+';
    };
    if (!isMobileNav() && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      let hoverTimeout;
      group.addEventListener('mouseenter', () => { clearTimeout(hoverTimeout); setOpen(true); });
      group.addEventListener('mouseleave', () => { hoverTimeout = setTimeout(() => setOpen(false), 150); });
    } else {
      /* Мобильный или грубый указатель: тап по триггеру раскрывает подменю
         без перехода на страницу раздела (иначе страница перезагрузится
         раньше, чем подменю можно рассмотреть). Первой ссылкой «Все
         направления» и последними ссылками на разделы остаётся сам путь
         навигации. */
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (group.getAttribute('data-dropdown-open') === 'true') {
          setOpen(false);
          return;
        }
        closeDropdowns();
        setOpen(true);
      });
    }
  });

  root.querySelectorAll('#main-navigation a').forEach((link) => {
    link.addEventListener('click', (event) => {
      // Клик по триггеру-аккордеону остаётся его: первый тап раскрывает,
      // следующий сворачивает. Ссылки-потомки всё равно переходят по href
      // и закрывают панель.
      if (isMobileNav() && !event.defaultPrevented) {
        closeDropdowns();
        closeMenu();
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDropdowns();
      closeMenu({ restoreFocus: true });
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target instanceof Node && !event.target.closest('[data-nav-group]')) closeDropdowns();
  });

  const desktopQuery = window.matchMedia(`(min-width: ${DESKTOP_NAV_MIN}px)`);
  const handleViewportChange = (event) => {
    if (event.matches) {
      closeMenu();
      closeDropdowns();
      if ('inert' in nav) nav.inert = false;
    } else if (nav.getAttribute('data-menu-open') !== 'true') {
      setNavInert(true);
    }
  };
  setNavInert(true);
  desktopQuery.addEventListener?.('change', handleViewportChange);
}

export function initStickyHeader(root = document) {
  const header = root.querySelector('[data-site-header]');
  if (!header) return;

  const nav = root.querySelector('#main-navigation');
  let lastY = Math.max(window.scrollY || 0, 0);
  let ticking = false;

  const render = () => {
    ticking = false;
    const currentY = Math.max(window.scrollY || 0, 0);
    const menuOpen = nav?.getAttribute('data-menu-open') === 'true';

    // За высотой всей шапки полоса сжимается: логотип и строка навигации
    // уменьшаются, чтобы липкая полоса съедала как можно меньше экрана,
    // а верхняя строка со слоганом и контактами остаётся.
    if (currentY > header.offsetHeight) header.setAttribute('data-header-compact', 'true');
    else header.removeAttribute('data-header-compact');

    if (menuOpen || currentY <= SCROLL_THRESHOLD) {
      header.removeAttribute('data-header-hidden');
    } else if (currentY > lastY + SCROLL_THRESHOLD) {
      header.setAttribute('data-header-hidden', 'true');
    } else if (currentY < lastY - SCROLL_THRESHOLD) {
      header.removeAttribute('data-header-hidden');
    }
    lastY = currentY;
  };

  const schedule = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(render);
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  render();
}

/**
 * Липкое оглавление длинной страницы: помечает раздел, который сейчас
 * занимает полосу под навигацией.
 *
 * Обычная позиция прокрутки, а не IntersectionObserver: разделы разной
 * высоты (на порядок), поэтому «последний заголовок, мимо которого мы
 * прошли» — это то, что читатель и ожидает увидеть подсвеченным.
 */
export function initAnchorNav(root = document) {
  const nav = root.querySelector('[data-anchor-nav]');
  if (!nav) return;
  const links = Array.from(nav.querySelectorAll('[data-anchor-link]'));
  const targets = links
    .map((link) => ({ link, section: root.querySelector(link.getAttribute('href')) }))
    .filter((entry) => entry.section);
  if (!targets.length) return;

  let ticking = false;
  const sync = () => {
    ticking = false;
    // Чуть ниже полосы: у якорей задан scroll-margin, поэтому только что
    // открытый раздел оказывается немного под ней.
    const line = nav.getBoundingClientRect().bottom + 24;
    // Выше первого якоренного раздела полоса иначе читалась бы как
    // инертная: читатель на открывающем разделе — помечаем его.
    let active = targets[0];
    for (const entry of targets) {
      if (entry.section.getBoundingClientRect().top <= line) active = entry;
    }
    for (const entry of targets) {
      if (entry === active) entry.link.setAttribute('aria-current', 'true');
      else entry.link.removeAttribute('aria-current');
    }
  };

  const schedule = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(sync);
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  sync();
}

/** Лайтбокс галереи на нативном `<dialog>`. */
export function initGalleries(root = document) {
  root.querySelectorAll('[data-gallery-lightbox]').forEach((dialog) => {
    if (dialog.dataset.galleryBound === 'true') return;
    dialog.dataset.galleryBound = 'true';
    const image = dialog.querySelector('[data-gallery-full-image]');
    const stage = dialog.querySelector('[data-gallery-stage]');
    const closeButton = dialog.querySelector('[data-gallery-close]');
    let opener = null;

    const close = () => {
      if (dialog.open) dialog.close();
    };

    root.querySelectorAll(`[data-gallery-open][data-gallery-dialog="${dialog.id}"]`).forEach((button) => {
      button.addEventListener('click', () => {
        opener = button;
        if (image) image.src = button.dataset.gallerySrc || '';
        dialog.showModal();
        closeButton?.focus();
      });
    });

    closeButton?.addEventListener('click', close);
    stage?.addEventListener('click', (event) => {
      if (event.target === stage) close();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    dialog.addEventListener('close', () => {
      if (image) image.removeAttribute('src');
      opener?.focus();
    });
  });
}

/**
 * Плавающая кнопка «Позвонить» на телефоне — круглая кнопка в правом
 * нижнем углу. Она повторяет основное действие первого экрана, поэтому
 * показывается только когда посетитель прокрутил ниже hero: у самого
 * первого экрана кнопка молчит, чтобы не дублировать его CTA. Hero на
 * каждой странице помечен `data-hero`.
 *
 * Отрицательный отступ сверху сдвигает линию срабатывания внутрь экрана:
 * кнопка проявляется, когда низ hero поднялся к верхней четверти окна, а
 * не ждёт, пока hero покинет кадр целиком. На высоком герое (главная с
 * видео) полный выход наступал бы слишком поздно.
 */
export function initMobileCta(root = document) {
  const cta = root.querySelector('.ax-mobile-cta');
  if (!cta) return;
  const hero = root.querySelector('[data-hero]');
  // Стартовое состояние — скрыта; покажем, только когда hero ушёл вверх.
  cta.toggleAttribute('data-cta-hidden', true);
  if (!hero) return;

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          cta.toggleAttribute('data-cta-hidden', entry.isIntersecting);
        });
      },
      { rootMargin: '-25% 0px 0px 0px' },
    );
    observer.observe(hero);
  }
}
